import * as BizCharts from 'bizcharts';
import React, { FC } from 'react';
import './chartTheme';
import { withNiceScale } from './chartUtils';

const { Chart, Axis, Geom, Tooltip } = BizCharts;

// Solve the problem of axis label overlapping with tick content at once! Noticed this in analytics too few days back 
const Y_AXIS_TITLE_MIN_OFFSET = 80;
const Y_AXIS_LABEL_CHAR_WIDTH = 7;
const Y_AXIS_TITLE_GAP = 24;

const getYAxisTitleOffset = (data: any, cols: any, yAxisName: string): number => {
	const yAxisCol = cols ? cols[yAxisName] : undefined;
	const valueFormatter = yAxisCol ? yAxisCol.formatter : undefined;
	const widestLabelLength = (data || []).reduce((widest: number, datum: any) => {
		const value = datum ? datum[yAxisName] : undefined;
		const label = valueFormatter ? valueFormatter(value) : value;
		return Math.max(widest, String(label === null || label === undefined ? '' : label).length);
	}, 0);
	return Math.max(
		Y_AXIS_TITLE_MIN_OFFSET,
		widestLabelLength * Y_AXIS_LABEL_CHAR_WIDTH + Y_AXIS_TITLE_GAP
	);
};

interface LineChartProps {
	cols: any;
	height: number;
	data: any;
	fillArea: boolean;
	position: string;
	chartPadding?: [number, number, number, number];
	xAxisLabel?: any;
	yAxisLabel?: any;
	color?: string;
	xAxisName: string;
	yAxisName: string;
	tooltipTemplate?: any;
	tooltipTitle?: string | ((title: string, datum: any) => string);
	xAxisTitle?: string;
	yAxisTitle?: string;
}

export const LineChart: FC<LineChartProps> = ({
	chartPadding,
	tooltipTemplate,
	tooltipTitle,
	data,
	cols,
	color,
	height,
	fillArea,
	xAxisName,
	yAxisName,
	xAxisLabel,
	yAxisLabel,
	position,
	xAxisTitle,
	yAxisTitle,
}) => {
	return (
		<Chart
			height={height}
			padding="auto"
			data={data}
			scale={withNiceScale(cols)}
			autoFit={true}
			theme="openwrench"
		>
			<Axis
				name={xAxisName}
				label={xAxisLabel}
				title={
					xAxisTitle
						? { text: xAxisTitle, style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' }, offset: 40 }
						: null
				}
			/>
			<Axis
				name={yAxisName}
				label={yAxisLabel}
				title={
					yAxisTitle
						? {
								text: yAxisTitle,
								style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
								offset: getYAxisTitleOffset(data, cols, yAxisName),
						  }
						: null
				}
			/>
			<Tooltip
				crosshairs={{ type: 'y' }}
				title={tooltipTitle}
				itemTpl={
					// A custom itemTpl has to carry G2's own dom classes
					tooltipTemplate
						? tooltipTemplate
						: `
                    <li class="g2-tooltip-list-item" data-index={index}>
                        <span class="g2-tooltip-marker" style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
                        {name}: {value}
                    </li>
                `
				}
			/>
			<Geom type="line" position={position} color={color} size={2} />
			{fillArea ? <Geom type="area" position={position} color={color} /> : null}
			<Geom
				type="point"
				position={position}
				color={color}
				size={4}
				shape={'circle'}
				style={{ stroke: '#fff', lineWidth: 1 }}
			/>
		</Chart>
	);
};
